import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MoreHorizontal, PenLine, ExternalLink, QrCode, BarChart3, Copy, Link2, Power, Archive, Send, RotateCcw } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { StatusBadge, Empty } from "@/components/admin/common";
import { api, formatErr, publicCardUrl, fileUrl, copyText } from "@/lib/api";

const IconBtn = ({ onClick, title, children, testId }) => (
  <button title={title} onClick={onClick} data-testid={testId} className="rounded-md p-2 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900">{children}</button>
);

export const CardsTable = ({ cards, onChanged, showClient = true }) => {
  const nav = useNavigate();
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const setStatus = (c, status, msg) => act(() => api.post(`/cards/${c.id}/status`, { status }), msg);

  if (!cards.length) return <Empty title="Nessuna card">Crea la prima card per iniziare.</Empty>;
  return (
    <div className="panel overflow-x-auto" data-testid="cards-table">
      <table className="w-full min-w-[760px] text-sm">
        <thead>
          <tr className="border-b text-left"><th className="eyebrow px-5 py-3 font-normal">Card</th>{showClient && <th className="eyebrow px-3 py-3 font-normal">Cliente</th>}<th className="eyebrow px-3 py-3 font-normal">Stato</th><th className="eyebrow px-3 py-3 font-normal">URL</th><th className="eyebrow hidden px-3 py-3 font-normal text-right xl:table-cell">Visite</th><th className="px-3 py-3" /></tr>
        </thead>
        <tbody>
          {cards.map((c) => (
            <tr key={c.id} className="border-b last:border-0 transition-colors hover:bg-zinc-50/70" data-testid={`card-row-${c.slug}`}>
              <td className="px-5 py-3.5">
                <button onClick={() => nav(`/admin/card/${c.id}/editor`)} className="flex items-center gap-3 text-left">
                  <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full border bg-zinc-100">{c.avatar_url && <img src={fileUrl(c.avatar_url)} alt="" className="h-full w-full object-cover" />}</div>
                  <div><div className="font-medium text-zinc-900">{c.display_name || c.name}</div><div className="text-xs text-zinc-500">{c.name} · {c.template}</div></div>
                </button>
              </td>
              {showClient && <td className="px-3 py-3.5 text-zinc-600">{c.client_name}</td>}
              <td className="px-3 py-3.5"><StatusBadge status={c.status} testId={`card-status-${c.slug}`} /></td>
              <td className="px-3 py-3.5">
                <button className="font-mono-ui text-xs text-zinc-500 hover:text-terra" data-testid={`card-copy-url-${c.slug}`}
                  onClick={async () => { await copyText(publicCardUrl(c.slug)); toast.success("URL copiato"); }}>/c/{c.slug}</button>
              </td>
              <td className="px-3 py-3.5 text-right tabular-nums hidden xl:table-cell" data-testid={`card-views-${c.slug}`}>{c.views}</td>
              <td className="sticky right-0 bg-white/95 px-3 py-3.5">
                <div className="flex items-center justify-end">
                  <IconBtn title="Editor" testId={`card-edit-${c.slug}`} onClick={() => nav(`/admin/card/${c.id}/editor`)}><PenLine className="h-4 w-4" strokeWidth={1.6} /></IconBtn>
                  <IconBtn title="QR code" testId={`card-qr-${c.slug}`} onClick={() => nav(`/admin/card/${c.id}/qr`)}><QrCode className="h-4 w-4" strokeWidth={1.6} /></IconBtn>
                  <IconBtn title="Statistiche" testId={`card-stats-${c.slug}`} onClick={() => nav(`/admin/card/${c.id}/statistiche`)}><BarChart3 className="h-4 w-4" strokeWidth={1.6} /></IconBtn>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button data-testid={`card-menu-${c.slug}`} className="rounded-md p-2 text-zinc-500 hover:bg-zinc-100"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem onClick={() => window.open(publicCardUrl(c.slug), "_blank")} data-testid={`card-open-public-${c.slug}`}><ExternalLink className="mr-2 h-4 w-4" />Apri landing</DropdownMenuItem>
                      <DropdownMenuItem onClick={async () => { await copyText(publicCardUrl(c.slug)); toast.success("URL copiato"); }}><Link2 className="mr-2 h-4 w-4" />Copia URL</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`card-duplicate-${c.slug}`} onClick={() => act(() => api.post(`/cards/${c.id}/duplicate`), "Card duplicata come bozza")}><Copy className="mr-2 h-4 w-4" />Duplica</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {c.status !== "published" && <DropdownMenuItem data-testid={`card-publish-${c.slug}`} onClick={() => setStatus(c, "published", "Card pubblicata")}><Send className="mr-2 h-4 w-4" />Pubblica</DropdownMenuItem>}
                      {c.status === "published" && <DropdownMenuItem data-testid={`card-disable-${c.slug}`} onClick={() => setStatus(c, "disabled", "Card disattivata")}><Power className="mr-2 h-4 w-4" />Disattiva</DropdownMenuItem>}
                      {c.status !== "archived" ? (
                        <DropdownMenuItem data-testid={`card-archive-${c.slug}`} onClick={() => setStatus(c, "archived", "Card archiviata")}><Archive className="mr-2 h-4 w-4" />Archivia</DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem data-testid={`card-restore-${c.slug}`} onClick={() => setStatus(c, "draft", "Card ripristinata come bozza")}><RotateCcw className="mr-2 h-4 w-4" />Ripristina</DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
